import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Fund } from "@/lib/api/acquisitions";
import type { TransferFormState } from "@/hooks/useBudgetOverview";

type BudgetTransferDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  funds: Fund[];
  transferData: TransferFormState;
  onTransferDataChange: (next: TransferFormState) => void;
  transferring: boolean;
  onTransfer: () => void;
  formatCurrency: (n: number) => string;
};

export function BudgetTransferDialog({
  open,
  onOpenChange,
  funds,
  transferData,
  onTransferDataChange,
  transferring,
  onTransfer,
  formatCurrency,
}: BudgetTransferDialogProps) {
  const activeFunds = funds.filter((f) => f.isActive);
  const patch = (partial: Partial<TransferFormState>) =>
    onTransferDataChange({ ...transferData, ...partial });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Transfer between funds</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          Moves budget authority from one fund to another. Total tenant budget stays the same.
        </p>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label required>From fund</Label>
            <Select value={transferData.fromFundId} onValueChange={(v) => patch({ fromFundId: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select source fund" />
              </SelectTrigger>
              <SelectContent>
                {activeFunds.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name} ({formatCurrency(f.available)} available)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label required>To fund</Label>
            <Select value={transferData.toFundId} onValueChange={(v) => patch({ toFundId: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select destination fund" />
              </SelectTrigger>
              <SelectContent>
                {activeFunds.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label required>Amount</Label>
            <Input
              type="number"
              step="0.01"
              value={transferData.amount}
              onChange={(e) => patch({ amount: e.target.value })}
              placeholder="0.00"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Reason (optional)</Label>
            <Input
              value={transferData.reason}
              onChange={(e) => patch({ reason: e.target.value })}
              placeholder="e.g. Reallocate unused print budget"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onTransfer} disabled={transferring}>
            {transferring && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
